// Module 4.1 — Kill Sheet schema (v1).

import { UNITS_VERSION } from "@/lib/openg/units";
import { FORMULAS_VERSION } from "@/lib/openg/formulas/pressures";
import { DRILLERS_SCHEDULE_VERSION } from "@/lib/openg/formulas/drillersSchedule";
import { getEngineeringDefaults } from "@/lib/storage";

export const KILLSHEET_SCHEMA_VERSION = 1;

// P0 fix (Phase 2a) — seed inputs.safetyMarginPpg / .scheduleIntervalStrokes
// from rsc.settings.v1.engineeringDefaults AT CREATION TIME, instead of
// leaving them blank (Phase 2) or hardcoding "0"/"100" (pre-Phase-2). A
// blank value reaches openg/formulas/pressures.js#kmwPpg() as an invisible
// silent zero (asNum(v, 0)) — that file is intentionally untouched here.
// Reading the Settings value directly into the seed closes that gap without
// touching any formula. Falls back to the existing documented default
// ("0.3" / "100" — see lib/storage.js#ENG_DEFAULTS_FACTORY) only when the
// setting is absent or blank, so a new sheet's safety margin is never blank.
function _seededEngineeringDefault(key, fallback) {
  let defaults = {};
  try { defaults = getEngineeringDefaults() || {}; } catch { defaults = {}; }
  const v = defaults[key];
  return (v !== "" && v != null) ? String(v) : fallback;
}

// ---------------------------------------------------------------------------
// Enums — Engineering Basis (Refinement 1)
// ---------------------------------------------------------------------------
export const KILL_METHODS = [
  "drillers-method",
  "wait-and-weight",
  "engineers-method",
  "volumetric",
  "bullheading",
  "other",
];

export const FLUID_BASES = ["water-based", "oil-based", "synthetic-based"];

export const INFLUX_TYPES = ["gas", "oil", "water", "unknown"];

// Optional industry training/regulatory standard the sheet was prepared
// against. Sheets prepared without an explicit standard simply leave this
// blank.
export const WELL_CONTROL_STANDARDS = [
  "",                // none / not specified
  "IWCF",
  "IADC-WellSharp",
  "API-RP-59",
  "API-RP-53",
  "API-Std-65-2",
  "NORSOK-D-010",
  "Operator-internal",
  "Other",
];

export const KILLSHEET_STATUSES = [
  "draft",
  "active",
  "approved",      // Pass E — approved by senior personnel; ready to lock
  "locked",        // Refinement 2 — once locked, edits force a revision
  "used",
  "superseded",
  "archived",
];

// Pass E — controlled vocabulary for "Reason for Revision". Optional —
// blank is allowed for legacy / informal revisions.
export const REVISION_REASONS = [
  "",                          // unspecified
  "updated-engineering-data",
  "updated-well-conditions",
  "corrected-input-data",
  "supervisor-review",
  "customer-request",
  "other",
];
export const REVISION_REASON_LABELS = {
  "":                            "— Not specified —",
  "updated-engineering-data":    "Updated Engineering Data",
  "updated-well-conditions":     "Updated Well Conditions",
  "corrected-input-data":        "Corrected Input Data",
  "supervisor-review":           "Supervisor Review",
  "customer-request":            "Customer Request",
  "other":                       "Other",
};

// ---------------------------------------------------------------------------
// Factories
// ---------------------------------------------------------------------------
export function emptyKillSheet() {
  return {
    id: "",
    schemaVersion: KILLSHEET_SCHEMA_VERSION,
    wellId: "",

    // Identity
    name: "",
    sheetNumber: "",
    revision: "",
    dateTime: "",
    preparedBy: "",

    // Pass E — Approval workflow
    approvedBy: "",
    approvedAt: null,
    approvalNotes: "",

    // Pass E — Revision lineage. parentId is the previous revision in the
    // chain (blank for the first revision). revisionReason carries the
    // controlled-vocabulary tag (see REVISION_REASONS).
    parentId: "",
    revisionReason: "",
    revisionNotes: "",

    // Engineering Basis (Refinement 1)
    basis: {
      killMethod: "drillers-method",
      fluidBasis: "water-based",
      influxType: "unknown",
      // Optional — industry standard the sheet was prepared against (blank = unspecified).
      wellControlStandard: "",
    },

    // Lineage — IDs only, never duplicated payloads
    source: { bhaId: "", drillStringId: "", rigSheetId: "" },

    // When the inputs were last refreshed from upstream sources (Well
    // Profile / BHA). Frozen until the user explicitly invokes
    // "Re-pull from sources" — see storage.js#rePullKillSheetFromSources.
    pullSnapshot: {
      pulledAt: null,
      pulledFrom: {
        wellId: "", bhaId: "", drillStringId: "", rigSheetId: "",
      },
    },

    // Frozen snapshot taken when the sheet was created (audit trail)
    inputs: {
      holeSize: "",
      casingShoeTvdFt: "",
      currentTvdFt: "",
      currentMdFt: "",
      mwInPpg: "",
      sidppPsi: "",
      sicpPsi: "",
      slowPumpPressurePsi: "",
      pumpRateSpm: "",
      // Phase 1 — Operational kick volume. Stored only; no calculator
      // consumes it yet. Reserved for the next release's influx-height /
      // influx-classification work.
      influxSizeBbl: "",
      pumpOutputBblStk: "",
      surfaceLineVolBbl: "",
      stringInternalVolBbl: "",
      bitToShoeVolBbl: "",
      shoeToSurfaceVolBbl: "",
      kickHeightFt: "",
      // P0 fix (Phase 2a) — seeded from Settings at creation time (see
      // _seededEngineeringDefault above), never blank and never a
      // hardcoded literal that would masquerade as a real user entry.
      safetyMarginPpg: _seededEngineeringDefault("safetyMarginPpg", "0.3"),
      // Pass B — operator-configurable stroke increment for the Driller's
      // Schedule. Seeded from Settings at creation time; falls back to the
      // documented default (100) when absent/blank.
      scheduleIntervalStrokes: _seededEngineeringDefault("scheduleIntervalStrokes", "100"),
      // Pass D — MAASP basis. lotPressurePsi: surface pressure recorded at
      // leak-off. mwAtLotPpg: mud density in the hole during the LOT.
      // Both optional — leaving either blank disables the MAASP computation
      // but the rest of the sheet still works.
      lotPressurePsi: "",
      mwAtLotPpg: "",
    },

    // RCE-1.0 Engineering Snapshot (Phase RCE-4) — frozen on first save,
    // refreshed only via the explicit "Re-pull from Context" action. Carries
    // the permanent Engineering Snapshot ID, source modules, confidence
    // map, and session-context lineage.
    contextSnapshot: {
      frozen: false,
      snapshotId: "",
      contextVersion: "RCE-1.0",
      capturedAt: null,
      project: { operator: "", client: "", country: "", field: "" },
      rig:     { name: "", contractor: "" },
      well:    {
        name: "", environment: "", waterDepthFt: "",
        holeSection: "", holeSizeIn: "",
        currentMdFt: "", currentTvdFt: "",
        casingShoeMdFt: "", casingShoeTvdFt: "",
      },
      bha: { id: "", revision: "" },
      drillString: { id: "", revision: "" },
      mud: { weightInPpg: "" },
      confidence: {},
      sessionContextIds: {
        projectId: "", rigId: "", wellId: "", bhaId: "", drillStringId: "", mudProgramId: "",
      },
      sourceModules: [],
    },

    // Last-displayed snapshot of computed outputs — for reproducibility.
    // Always re-computed live on read by compute.js.
    outputs: {
      kmwPpg: "", icpPsi: "", fcpPsi: "",
      strokesSurfaceToBit: "", strokesBitToShoe: "", strokesShoeToSurface: "",
      timeSurfaceToBitMin: "", timeBitToShoeMin: "", timeShoeToSurfaceMin: "",
      notes: [],
      // Refinement 3 — calculation provenance
      provenance: {
        formulasVersion: FORMULAS_VERSION,
        unitsVersion: UNITS_VERSION,
        scheduleVersion: DRILLERS_SCHEDULE_VERSION,
        computedAt: null,
      },
    },

    status: "draft",
    lockedAt: null,                 // Refinement 2
    lockedBy: "",
    notes: "",
    isActive: true,
    createdAt: null,
    updatedAt: null,
  };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------
const _opt = (v) => v === "" || v == null || Number.isFinite(Number(v));
const _optPos = (v) => v === "" || v == null || (Number.isFinite(Number(v)) && Number(v) > 0);

export function validateKillSheet(rec) {
  if (!rec || typeof rec !== "object")
    return { ok: false, errors: ["Kill sheet is missing."] };
  const errs = [];
  if (!rec.wellId) errs.push("Kill sheet must be linked to a well.");
  if (!rec.name || !String(rec.name).trim()) errs.push("Sheet name is required.");
  if (!KILLSHEET_STATUSES.includes(rec.status))
    errs.push(`Status must be one of: ${KILLSHEET_STATUSES.join(", ")}.`);
  if (rec.basis) {
    if (!KILL_METHODS.includes(rec.basis.killMethod))
      errs.push(`Kill method must be one of: ${KILL_METHODS.join(", ")}.`);
    if (!FLUID_BASES.includes(rec.basis.fluidBasis))
      errs.push(`Fluid basis must be one of: ${FLUID_BASES.join(", ")}.`);
    if (!INFLUX_TYPES.includes(rec.basis.influxType))
      errs.push(`Influx type must be one of: ${INFLUX_TYPES.join(", ")}.`);
    if (rec.basis.wellControlStandard !== undefined &&
        !WELL_CONTROL_STANDARDS.includes(rec.basis.wellControlStandard))
      errs.push(`Well control standard must be one of: ${WELL_CONTROL_STANDARDS.filter(Boolean).join(", ")} (or blank).`);
  } else {
    errs.push("Engineering basis is required.");
  }
  // Soft numeric checks — all inputs may be blank in draft state.
  for (const [k, label] of [
    ["mwInPpg", "Mud weight"],
    ["sidppPsi", "SIDPP"], ["sicpPsi", "SICP"],
    ["slowPumpPressurePsi", "Slow-pump pressure"],
    ["pumpRateSpm", "Pump rate"], ["pumpOutputBblStk", "Pump output"],
    ["influxSizeBbl", "Size of influx"],
    ["currentTvdFt", "Current TVD"], ["currentMdFt", "Current MD"],
    ["casingShoeTvdFt", "Casing shoe TVD"], ["holeSize", "Hole size"],
    ["surfaceLineVolBbl", "Surface line vol"],
    ["stringInternalVolBbl", "String internal vol"],
    ["bitToShoeVolBbl", "Bit→shoe vol"],
    ["shoeToSurfaceVolBbl", "Shoe→surface vol"],
    ["safetyMarginPpg", "Safety margin"],
  ]) {
    if (!_opt(rec.inputs?.[k]))
      errs.push(`${label}: must be a number if entered.`);
  }
  // Pass E — revision reason enum (optional, blank allowed).
  if (rec.revisionReason !== undefined && rec.revisionReason !== "" &&
      !REVISION_REASONS.includes(rec.revisionReason)) {
    errs.push(`Revision reason must be one of: ${REVISION_REASONS.filter(Boolean).join(", ")} (or blank).`);
  }
  return { ok: errs.length === 0, errors: errs };
}
